import { Grid, Stack } from "@mui/material";
import { useFormik } from "formik";
import React from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  ControlledCustomSelect,
  CustomFormLabel,
  CustomTextField,
} from "../../../../components/formsComponents";
import { IStoreState } from "../../../../redux/initialStoreState";

import { PageLoader } from "../../../../components/PageLoader/PageLoader";
import { PhoneTextField } from "../../../../components/PhoneTextField/PhoneTextField";
import { LoadState, MODULE_IDS } from "../../../../constants/enums";
import {
  addPersonalInformationAsync,
} from "../../../../redux/customer/customerActions";
import { IPersonalInformationProps } from "./PersonalInformation.types";
import { RoleBasedCustomButton } from "../../../../security/RoleBasedComponents/RoleBasedCustomButton/RoleBasedCustomButton";
import { useNavigate } from "react-router-dom";
import { AddressSearchAutoComplete } from "../../../../components/AddressAutoComplete/AddressSearchAutoComplete";
import { BranchCodeAutoSearch } from "../../../../components/AutoCompleteSearches/BranchCodeAutoSearch";
import { UsersAutoSearchByRole } from "../../../../components/AutoCompleteSearches/UsersAutoSearchByRole";
import { ExtendableSelect } from "../../../../components/formsComponents/ExtendableSelect";
import { form_of_buisness } from "../../../../constants/constants";
import { CustomDatePicker } from "../../../../components/formsComponents/CustomDatePicker";
import { ILocationResponsePayload } from "../../../../components/LocationAutoComplete/LocationAutoComplete";
import { LabelCard } from "../../../../components/LabelCard/LabelCard";
import { normalizeEmail, validateEmailField } from "../../../../helpers/email";

export const PersonalInformation: React.FC<IPersonalInformationProps> = (
  props
) => {
  const { onSaveComplete } = props;

  const { data, loading } = useSelector(
    (storeState: IStoreState) => storeState.customer.personalInformation
  );
  const [saveLoading, setSaveLoading] = React.useState(false);
  const [buttonClicked, setButtonClicked] = React.useState<string | null>(null);
  const navigate = useNavigate();
  const dispatch = useDispatch<any>();

  const {
    values,
    errors,
    handleChange,
    handleSubmit,
    setValues,
    setFieldValue,
  } = useFormik({
    initialValues: { ...data },
    validate: (values) => {
      const errors: any = {};
      const emailError = validateEmailField(values.email, "*Please enter a valid email address.");
      if (emailError) {
        errors.email = emailError;
      }
      if (values.phone && values.phone.length !== 10) {
        errors.phone = "*Phone number should be 10 digits.";
      } else if (!values.branch_code) {
        errors.branch_code = "Branch is required!";
      } else if (!values.producer_code) {
        errors.producer_code = "Producer is required!";
      }
      return errors;
    },
    onSubmit: (values) => {
      setSaveLoading(true);
      dispatch(
        addPersonalInformationAsync(
          { ...values, email: normalizeEmail(values.email) },
          { ...data},
          true,
          (isSuccess) => {
            if (isSuccess) {
             
                onSaveComplete();
             
            }
            setSaveLoading(false);
          }
        )
      );
    },
  });

  const handleAddress = (data: ILocationResponsePayload) => {
    // Only overwrite the dependent fields when the incoming value is present.
    // Typing manually in the Address box sends empty city/state/postal/country,
    // which previously wiped those fields. Merging conditionally prevents that.
    setValues({
      ...values,
      mailing_address: data.address,
      ...(data.city ? { city: data.city } : {}),
      ...(data.country ? { country: data.country } : {}),
      ...(data.state ? { province_or_state: data.state } : {}),
      ...(data.postalCode ? { postal_code: data.postalCode } : {}),
    });
  };

  const handleClearPosition = () => {
    setValues({ ...values, position: "" });
  };
  const handleClearSalutation = () => {
    setValues({ ...values, salutation: "" });
  };

  React.useEffect(() => {
    setValues({ ...data });
    // Only re-sync the form when the underlying customer record changes.
    // Depending on the whole `data` object caused the form to reset on every
    // store update, which wiped values (city/state/postal/country) while typing.
     
  }, [data.customer_id]);



  return (
    <PageLoader loading={loading === LoadState.InProgress}>
   
        <LabelCard heading="Personal Information"/>
        <form onSubmit={handleSubmit}>
          <Grid container spacing={1}>
            <Grid item xs={12} lg={1}>
              <CustomFormLabel>Salutation</CustomFormLabel>
              <ControlledCustomSelect
                fullWidth
                value={values.salutation}
                name="salutation"
                onChange={handleChange}
                placeholder="Select One"
                displayEmpty
                clearable
                onClear={handleClearSalutation}
                options={["Mr.", "Mrs.", "Ms.", "Miss", "Dr.", "Prof."].map(
                  (template) => {
                    return { label: template, value: template };
                  }
                )}
              ></ControlledCustomSelect>
            </Grid>

            <Grid item xs={12} lg={2}>
              <CustomFormLabel>First Name</CustomFormLabel>
              <CustomTextField
                id="first_name"
                variant="outlined"
                size="small"
                type="text"
                fullWidth
                value={values.first_name}
                error={errors.first_name ? true : false}
                helperText={errors.first_name}
                onChange={handleChange}
              />
            </Grid>

            <Grid item xs={12} lg={2}>
              <CustomFormLabel>Last Name</CustomFormLabel>
              <CustomTextField
                id="last_name"
                variant="outlined"
                size="small"
                type="text"
                fullWidth
                value={values.last_name}
                onChange={handleChange}
              />
            </Grid>
               <Grid item xs={12} lg={2}>
              <CustomFormLabel>Goes By</CustomFormLabel>
              <CustomTextField
                id="goes_by"
                variant="outlined"
                size="small"
                type="text"
                fullWidth
                value={values.goes_by}
                onChange={handleChange}
              />
            </Grid>

            <Grid item xs={12} lg={2}>
              <CustomFormLabel>Position</CustomFormLabel>
              <Stack direction="row" spacing={1}>
                <ControlledCustomSelect
                  fullWidth
                  value={values.position}
                  name="position"
                  onChange={handleChange}
                  placeholder="Select One"
                  displayEmpty
                  clearable
                  onClear={handleClearPosition}
                  options={[
                    "President",
                    "Director",
                    "CEO",
                    "CTO",
                    "COO",
                    "CFO",
                    "General Manager",
                    "Fleet Manager",
                    "Account Manager",
                    "Safety Manager",
                    "Supervisor",
                    "Associate",
                  ].map((template) => {
                    return { label: template, value: template };
                  })}
                ></ControlledCustomSelect>
              </Stack>
            </Grid>
            <Grid item xs={12} lg={3}>
              <CustomFormLabel>Email</CustomFormLabel>
              <CustomTextField
                id="email"
                variant="outlined"
                size="small"
                type="email"
                fullWidth
                value={values.email}
                error={errors.email ? true : false}
                helperText={errors.email}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12} lg={2}>
              <CustomFormLabel>Phone Number (+1)</CustomFormLabel>
              <PhoneTextField
                id="phone"
                variant="outlined"
                size="small"
                fullWidth
                value={values.phone}
                error={errors.phone ? true : false}
                helperText={errors.phone}
                setFieldValue={setFieldValue}
              />
            </Grid>
            <Grid item xs={12} lg={1}>
              <CustomFormLabel>Ext.</CustomFormLabel>
              <CustomTextField
                id="phone_ext"
                variant="outlined"
                size="small"
                type="tel"
                fullWidth
                value={values.phone_ext}
                onChange={handleChange}
              />
            </Grid>

            <Grid item xs={12} lg={2}>
              <CustomFormLabel>Mobile Number</CustomFormLabel>
              <PhoneTextField
                name="mobile"
                variant="outlined"
                size="small"
                fullWidth
                value={values.mobile}
                error={errors.mobile ? true : false}
                helperText={errors.mobile}
                setFieldValue={setFieldValue}
              />
            </Grid>
             <Grid item xs={12} lg={2}>
              <CustomFormLabel>Status</CustomFormLabel>
              <ControlledCustomSelect
                fullWidth
                value={values.status}
                name="status"
                onChange={handleChange}
                placeholder="Select One"
                displayEmpty
                options={[
                  { label: "Active", value: "ACTIVE" },
                  { label: "Inactive", value: "INACTIVE" },
                  { label: "Opportunity", value: "OPPORTUNITY" },
                ].map((data) => {
                  return { label: data.label, value: data.value };
                })}
              ></ControlledCustomSelect>
            </Grid>
               <Grid item xs={12} lg={2}>
              <CustomFormLabel>User Name</CustomFormLabel>
              <CustomTextField
                name="firstname_and_primaryid"
                variant="outlined"
                size="small"
                fullWidth
                disabled
                value={values.firstname_and_primaryid}
               
              />
            </Grid>

         
          </Grid>

          <LabelCard sx={{ mt: 2 }} heading="Customer" />

          <Grid container spacing={1}>
            <Grid item xs={12} lg={3}>
              <CustomFormLabel>Customer Name</CustomFormLabel>
              <CustomTextField
                id="customer_name"
                variant="outlined"
                size="small"
                type="text"
                fullWidth
                value={values.customer_name}
                error={errors.customer_name ? true : false}
                helperText={errors.customer_name}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12} lg={2}>
              <CustomFormLabel>Customer Code</CustomFormLabel>
              <CustomTextField
                id="customer_code"
                variant="outlined"
                size="small"
                type="text"
                fullWidth
                value={values.customer_code}
                error={errors.customer_code ? true : false}
                helperText={errors.customer_code}
                onChange={handleChange}
              />
            </Grid>

            <Grid item xs={12} lg={2}>
              <CustomFormLabel>Form of Business</CustomFormLabel>
              <ExtendableSelect
                placeholder="Select one"
                value={values.form_of_business}
                options={form_of_buisness.map((business) => {
                  return { label: business.label, value: business.value };
                })}
                onChange={(newValue) => {
                  setFieldValue("form_of_business", newValue);
                }}
              />
            </Grid>
            <Grid item xs={12} lg={2}>
              <CustomFormLabel>Sector/Status Code</CustomFormLabel>
              <ExtendableSelect
                placeholder="Select one"
                value={values.sector_status_code}
                options={["CAN", "CLI", "FIN", "GRO", "HTD", "INS", "PRO"].map(
                  (item) => {
                    return { label: item, value: item };
                  }
                )}
                onChange={(newValue) => {
                  setFieldValue("sector_status_code", newValue);
                }}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <CustomFormLabel>Customer Since</CustomFormLabel>
              <CustomDatePicker
                fullWidth
                value={values.customer_since || ""}
                onChange={(newValue) => {
                  setFieldValue("customer_since", newValue);
                }}
              />
            </Grid>
            <Grid item xs={12} lg={1}>
              <CustomFormLabel>Tax Zone</CustomFormLabel>

              <ControlledCustomSelect
                fullWidth
                value={values.tax_zone}
                name="tax_zone"
                onChange={handleChange}
                placeholder="Select One"
                displayEmpty
                options={[
                  "AB",
                  "BC",
                  "MB",
                  "NB",
                  "NL",
                  "NS",
                  "NT",
                  "NU",
                  "ON",
                  "PE",
                  "QC",
                  "SK",
                  "YT",
                ].map((template) => {
                  return { label: template, value: template };
                })}
              ></ControlledCustomSelect>
            </Grid>

            <Grid item xs={12} lg={3}>
              <AddressSearchAutoComplete
                address={values.mailing_address}
                onAddressChange={handleAddress}
              />
            </Grid>
            <Grid item xs={12} lg={2}>
              <CustomFormLabel>Unit/Suite</CustomFormLabel>
              <CustomTextField
                id="unit_or_suite"
                variant="outlined"
                size="small"
                type="text"
                fullWidth
                value={values.unit_or_suite}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12} lg={2}>
              <CustomFormLabel>City</CustomFormLabel>
              <CustomTextField
                id="city"
                variant="outlined"
                size="small"
                type="text"
                fullWidth
                value={values.city}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12} lg={2}>
              <CustomFormLabel>State/Province</CustomFormLabel>
              <CustomTextField
                id="province_or_state"
                variant="outlined"
                size="small"
                type="text"
                fullWidth
                value={values.province_or_state}
                onChange={handleChange}
              />
            </Grid>

            <Grid item xs={12} lg={1.5}>
              <CustomFormLabel>Postal Code</CustomFormLabel>
              <CustomTextField
                id="postal_code"
                variant="outlined"
                size="small"
                type="text"
                fullWidth
                value={values.postal_code}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12} lg={1.5}>
              <CustomFormLabel>Country</CustomFormLabel>
              <CustomTextField
                id="country"
                variant="outlined"
                size="small"
                type="text"
                fullWidth
                value={values.country}
                onChange={handleChange}
              />
            </Grid>

            <Grid item xs={12} lg={2}>
              <CustomFormLabel>Language</CustomFormLabel>
              <ControlledCustomSelect
                fullWidth
                value={values.language}
                name="language"
                onChange={handleChange}
                placeholder="Select One"
                displayEmpty
                options={["E", "F"].map((template) => {
                  return { label: template, value: template };
                })}
              ></ControlledCustomSelect>
            </Grid>

            <Grid item xs={12} lg={2}>
              <CustomFormLabel>Branch</CustomFormLabel>
              <BranchCodeAutoSearch
                value={values.branch_code}
                onSelect={(value) => {
                  setFieldValue("branch", value.label);
                  setFieldValue("branch_code", value.value);
                }}
                errorMessage={errors.branch_code}
              />
            </Grid>

            <Grid item xs={12} lg={2}>
              <UsersAutoSearchByRole
                label="Producer"
                value={values.producer}
                selectionBy="user_fact_id"
                errorMessage={errors.producer_code}
                onSelect={(user) => {
                  setFieldValue("producer", user.user_fact_id);
                  setFieldValue("producer_code", user.user_code);
                }}
              />
            </Grid>
            <Grid item xs={12} lg={2}>
              <UsersAutoSearchByRole
                label="CSR"
                value={values.csr}
                selectionBy="user_fact_id"
                onSelect={(user) => {
                  setFieldValue("csr", user.user_fact_id);
                  setFieldValue("csr_code", user.user_code);
                }}
              />
            </Grid>

            <Grid item xs={12} lg={2}>
              <UsersAutoSearchByRole
                label="Marketer"
                value={values.marketer}
                onSelect={(user) => {
                  setFieldValue("marketer", user.user_fact_id);
                  setFieldValue("marketer_code", user.user_code);
                }}
              />
            </Grid>

            <Grid item xs={12} lg={2}>
              <UsersAutoSearchByRole
                label="Assigned To"
                value={values.assigned_to_id}
                onSelect={(user) => {
                  setValues({
                    ...values,
                    assigned_to_code: user.user_code,
                    assigned_to_id: user.user_fact_id as any
                  })
                }}
              />
            </Grid>

            <Grid item xs={12} lg={2}>
              <CustomFormLabel>Previous Producer Code</CustomFormLabel>
              <CustomTextField
                id="previous_producer_code"
                variant="outlined"
                size="small"
                type="text"
                fullWidth
                value={values.previous_producer_code}
                disabled
              />
            </Grid>
           
          </Grid>
          <LabelCard sx={{ mt: 2 }} heading="Customer Social" />
          <Grid container spacing={2}>
            <Grid item xs={12} lg={4}>
              <CustomFormLabel>Website</CustomFormLabel>
              <CustomTextField
                id="website"
                variant="outlined"
                size="small"
                type="text"
                fullWidth
                placeholder="https://example.ca"
                helperText={errors.website}
                onChange={handleChange}
              />
            </Grid>

            <Grid item xs={12} lg={4}>
              <CustomFormLabel>Facebook</CustomFormLabel>
              <CustomTextField
                id="facebook"
                variant="outlined"
                size="small"
                type="text"
                fullWidth
                placeholder="https://facebook.com/example.ca"
                value={values.facebook}
                onChange={handleChange}
              />
            </Grid>

            <Grid item xs={12} lg={4}>
              <CustomFormLabel>Linkedin</CustomFormLabel>
              <CustomTextField
                id="linkedin"
                variant="outlined"
                size="small"
                type="text"
                fullWidth
                placeholder="https://linkedin.com/example.ca"
                value={values.linkedin}
                onChange={handleChange}
              />
            </Grid>
          </Grid>
          <Stack direction={"row"} spacing={3} sx={{ mt: 4 }}>
            <RoleBasedCustomButton
              sx={{ width: "15%" }}
              disabled={saveLoading}
              variant="contained"
              moduleKey={MODULE_IDS.CUSTOMER as string}
              type="submit"
              onClick={() => setButtonClicked("save")}
            >
              Save
            </RoleBasedCustomButton>
           
          </Stack>
        </form>
    </PageLoader>
  );
};
